import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";
import { Link, useParams, useLocation } from "react-router-dom";
import { Row, Col, Tab, Tabs, Form, Spinner, Alert, Modal, Badge } from "react-bootstrap";
import { LegendFieldComponent, LegendTextareaComponent, IconFieldComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import { FileUploadComponent } from "../../../components";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";
import { FiUser, FiEdit2, FiDollarSign } from "react-icons/fi";
import { FaPaw } from "react-icons/fa";
import PetProceduresComponent from "../../../components/Admin/PetProceduresComponent";
import PetHistoryComponent from "../../../components/Admin/PetHistoryComponent";
import PetPhysicalExamComponent from "../../../components/Admin/PetPhysicalExamComponent";
import PetDiagnoseComponent from "../../../components/Admin/PetDiagnoseComponent";
import PetSurgeryComponent from "../../../components/Admin/PetSurgeryComponent";
import PetVaccineComponent from "../../../components/Admin/PetVaccineComponent";
import PetDewormingComponent from "../../../components/Admin/PetDewormingComponent";
import PetLabTestComponent from "../../../components/Admin/PetLabTestComponent";
import PetGroomingComponent from "../../../components/Admin/PetGroomingComponent";
import PetFollowUpComponent from "../../../components/Admin/PetFollowUpComponent";
import PetCheckUpComponent from "../../../components/Admin/PetCheckUpComponent";
import PetRadiologyComponent from "../../../components/Admin/PetRadiologyComponent";
import PetInhousePrescriptionComponent from "../../../components/Admin/PetInhousePrescriptionComponent";
import PetHomePrescriptionComponent from "../../../components/Admin/PetHomePrescriptionComponent";
import PetInvoiceComponent from "../../../components/Admin/PetInvoiceComponent";
import Swal from "sweetalert2";
import moment from "moment";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import CustomerSaleComponent from "../../../components/Admin/SaleCreatecomponent";

export default function AppointmentShowPage() {
    const { t } = useContext(TranslatorContext);
    const { setSidebar } = useContext(SidebarContext);
    const { id } = useParams();
    const location = useLocation();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("consultation");
    const AuthToken = localStorage.getItem("authToken");

    // Customer Balance States
    const [customerBalance, setCustomerBalance] = useState(null);
    const [saleBalance, setSaleBalance] = useState(null);
    const [totalBalance, setTotalBalance] = useState(0);
    const [loadingBalance, setLoadingBalance] = useState(false);

    // Edit Pet Modal States
    const [editPetModal, setEditPetModal] = useState(false);
    const [breeds, setBreeds] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [updateForm, setUpdateForm] = useState({
        id: null,
        name: '',
        breed_id: '',
        customer_id: '',
        age: '',
        color: '',
        male: '1',
        is_dead: false,
        birth_date: '',
        death_date: '',
        weight: '',
    });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Fetch Customer Balance from both APIs
    const fetchCustomerBalances = async (customerId) => {
        if (!customerId) return;

        setLoadingBalance(true);
        try {
            // 1. Fetch Appointment Ledger Balance
            const appointmentResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments/ledger/${customerId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            // 2. Fetch Sale Balance
            const saleResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-balance/${customerId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            const appointmentBalance = appointmentResponse.data?.total_balance || 0;
            const saleBalanceAmount = saleResponse.data?.total_balance || 0;

            setCustomerBalance(appointmentBalance);
            setSaleBalance(saleBalanceAmount);
            setTotalBalance(appointmentBalance + saleBalanceAmount);

        } catch (error) {
            console.error("Error fetching balances:", error);
            setCustomerBalance(0);
            setSaleBalance(0);
            setTotalBalance(0);
        } finally {
            setLoadingBalance(false);
        }
    };

    // Map procedure names to tab keys
    const getProcedureTabMapping = (procedureName) => {
        if (!procedureName) return "consultation";

        const nameLC = procedureName.toLowerCase().trim();

        // Direct mapping for known procedure names
        const tabMapping = {
            "surgery": "surgery",
            "vaccine": "vaccine",
            "vaccination": "vaccine",
            "deworming": "deworming",
            "grooming": "grooming",
            "follow-up": "follow-up",
            "follow up": "follow-up",
            "followup": "follow-up",
            "lab test": "test",
            "labtest": "test",
            "test": "test",
            "radiology": "radiology",
            "x-ray": "radiology",
            "xray": "radiology",
            "prescription": "prescription",
            "physical exam": "consultation",
            "diagnose": "consultation",
            "diagnosis": "consultation",
            "history": "consultation"
        };

        return tabMapping[nameLC] || "consultation";
    };

    useEffect(() => {
        setSidebar(false);
        fetchAppointmentDetails();
    }, [AuthToken, setSidebar]);

    useEffect(() => {
        fetchAppointmentDetails();

        // If procedure name was passed via location state, set the tab
        if (location.state?.procedureName) {
            const tabKey = getProcedureTabMapping(location.state.procedureName);
            setActiveTab(tabKey);
        }
    }, [id]);

    // Fetch balances when appointment loads
    useEffect(() => {
        if (appointment?.customer_id) {
            fetchCustomerBalances(appointment.customer_id);
        }
    }, [appointment?.customer_id]);

    const fetchAppointmentDetails = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/appointments-show/${id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.appointment) {
                setAppointment(response.data.appointment);
                setError(null);
            }
        } catch (err) {
            console.error("Error fetching appointment:", err);
            setError("Failed to load appointment details");
        } finally {
            setLoading(false);
        }
    };

    // Fetch breeds and customers for edit modal
    const fetchFilterData = async () => {
        try {
            const AuthToken = localStorage.getItem("authToken");
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/pets`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            setBreeds(res.data.breeds);
            setCustomers(res.data.customers);
        } catch (err) {
            console.error(err);
        }
    };

    // Open Edit Pet Modal
    const openEditPetModal = async () => {
        try {
            const AuthToken = localStorage.getItem("authToken");
            await fetchFilterData();

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/pets-edit/${appointment.pet_id}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            setUpdateForm({
                id: res.data.pet.id,
                name: res.data.pet.name || '',
                breed_id: res.data.pet.breed_id || '',
                customer_id: res.data.pet.customer_id || '',
                age: res.data.pet.age || '',
                color: res.data.pet.color || '',
                male: res.data.pet.male ? '1' : '0',
                is_dead: res.data.pet.is_dead || false,
                birth_date: res.data.pet.birth_date || '',
                death_date: res.data.pet.death_date || '',
                weight: res.data.pet.weight || ''
            });
            setUpdateErrors({});
            setEditPetModal(true);
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to load pet details",
            });
        }
    };

    const handleUpdateChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUpdateForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (updateErrors[name]) setUpdateErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        const submitData = {
            ...updateForm,
            male: parseInt(updateForm.male)
        };

        try {
            const AuthToken = localStorage.getItem("authToken");
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets-update/${updateForm.id}`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (res.data.success) {
                await fetchAppointmentDetails();
                setEditPetModal(false);
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Pet updated successfully!'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setUpdateErrors(err.response.data.errors || {});
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update pet'),
                });
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">{t('loading')}</p>
                </div>
            </PageLayout>
        );
    }

    if (error) {
        return (
            <PageLayout>
                <Alert variant="danger" className="m-4">
                    {error}
                </Alert>
            </PageLayout>
        );
    }

    if (!appointment) {
        return (
            <PageLayout>
                <Alert variant="warning" className="m-4">
                    Appointment not found
                </Alert>
            </PageLayout>
        );
    }

    const isAppointmentOpen = appointment.status && !['completed', 'cancelled', 'missed'].includes(appointment.status.toLowerCase());

    const handleInvoiceClick = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/invoice-check/${appointment.id}`
            );

            if (!response.data.success) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.data.message || "Something went wrong",
                });
                return;
            }

            window.open(
                `${process.env.REACT_APP_URL}/invoice-print/${appointment.id}`,
                "_blank"
            );

        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Request Failed",
                text:
                    error.response?.data?.message ||
                    error.message ||
                    "Server error",
            });
        }
    };

    return (
        <PageLayout>
            {/* Customer Balance Card - Top of Page */}
            {/* {totalBalance > 0 && !loadingBalance && (
                <Row className="mb-4">
                    <Col xl={12}>
                        <Alert
                            variant="warning"
                            className="mb-0"
                            style={{
                                borderLeft: '4px solid #ffc107',
                                backgroundColor: '#fff8e7'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-warning bg-opacity-25 p-2 rounded-circle">
                                        <FiDollarSign size={24} color="#856404" />
                                    </div>
                                    <div>
                                        <h6 className="mb-1 fw-bold">{t('Customer Balance Summary')}</h6>
                                        <div className="d-flex flex-wrap gap-3">
                                            <Badge bg="warning" className="p-2">
                                                {t('Appointment Balance')}: {parseFloat(customerBalance || 0)}
                                            </Badge>
                                            <Badge bg="info" className="p-2">
                                                {t('Sale Balance')}: {parseFloat(saleBalance || 0)}
                                            </Badge>
                                            <Badge bg="danger" className="p-2">
                                                {t('Total Balance')}: {parseFloat(totalBalance)}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-muted small">
                                    <i className="bi bi-info-circle"></i> {t('Customer has pending payments')}
                                </div>
                            </div>
                        </Alert>
                    </Col>
                </Row>
            )} */}

            {loadingBalance && (
                <Row className="mb-4">
                    <Col xl={12}>
                        <Alert variant="secondary" className="mb-0">
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('Loading customer balance...')}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">{t('Appointments details')} </h3> <h3>
                                Reason: {appointment.pet_procedure?.[0]?.procedure?.name || 'N/A'}
                            </h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to='#' className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to='/appointments' className="mc-breadcrumb-link">{t('appointments')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('Appointments details')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <div className="d-flex flex-wrap gap-2 justify-content-end mb-4">
                    <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: '#004d4d' }}
                        onClick={() =>
                            window.open(
                                `${process.env.REACT_APP_URL}/appointment-print/${appointment.id}`,
                                "_blank"
                            )
                        }
                    >
                        <i className="mc-icon"></i> {t('appointment prescription')}
                    </button>
                </div>

                <Col xl={12}>
                    <div className="mc-card p-4">
                        <Row>
                            {/* Client/Customer Column */}
                            <Col md={6} className="border-end">
                                <div className="d-flex align-items-center mb-3">
                                    <div
                                        className="d-flex align-items-center justify-content-center me-2"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            background: '#e2e8f0'
                                        }}
                                    >
                                        <FiUser size={24} color="#4a5568" />
                                    </div>
                                    <h4 className="m-0">
                                        {appointment.customer?.name || 'N/A'}
                                    </h4>
                                </div>
                                <h6 className="text-muted mb-3">{t('client information')}</h6>
                                <div className="mc-pet-info-grid">
                                    <p><strong>{t('email')}:</strong> {appointment.customer?.email || 'N/A'}</p>
                                    <p><strong>{t('phone')}:</strong> {appointment.customer?.phone || 'N/A'}</p>
                                    <p><strong>{t('address')}:</strong> {appointment.customer?.address || 'N/A'}</p>
                                </div>
                            </Col>

                            {/* Pet Column */}
                            <Col md={6} className="ps-md-4">
                                <div className="d-flex align-items-center mb-3">
                                    <div
                                        className="d-flex align-items-center justify-content-center me-2"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            background: '#e2e8f0'
                                        }}
                                    >
                                        <FaPaw size={22} color="#4a5568" />
                                    </div>
                                    <h4 className="m-0">{appointment.pet?.name || 'N/A'}</h4>
                                </div>

                                {/* Edit Button after pet information */}

                                <div className="mb-3">
                                    <ButtonComponent
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={openEditPetModal}
                                    >
                                        <FiEdit2 size={16} className="me-1" />
                                        {t('Edit Pet')}
                                    </ButtonComponent>
                                </div>


                                <h6 className="text-muted mb-3">{t('pet information')}</h6>
                                <div className="mc-pet-info-grid">
                                    <p><strong>{t('color')}:</strong> {appointment.pet?.color || 'N/A'}</p>
                                    <p>
                                        <strong>{t('gender')}:</strong>
                                        {appointment.pet?.male === 1 ? 'Male' :
                                            appointment.pet?.male === 0 ? 'Female' : 'N/A'}
                                    </p>
                                    <p><strong>{t('breed')}:</strong> {appointment.pet?.breed?.name || 'N/A'}</p>
                                    <p><strong>{t('dob')}:</strong> {moment(appointment.pet?.birth_date ?? 'N/A').format("DD MMM YYYY")}</p>
                                    {appointment.pet?.weight && (
                                        <p><strong>{t('Weight')}:</strong> {appointment.pet.weight} kg</p>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            id="mc"
                            className="mc-tabs "
                            mountOnEnter={true}
                            unmountOnExit={false}
                            style={{ borderBottom: '1px solid black', paddingBottom: '20px', gap: '5px' }}
                        >
                            {/* Consultation Tab with nested tabs */}
                            <Tab eventKey="consultation" title={t('consultation')} className="mc-tabpane">
                                <div className="mt-1 ">
                                    <Tabs
                                        defaultActiveKey="history"
                                        id="consultation-subtabs"
                                        className="mc-tabs"
                                        fill
                                        style={{ gap: '5px' }}
                                        justify
                                        mountOnEnter={true}
                                        unmountOnExit={false}
                                    >
                                        {hasPermission("Appointment History view") && (
                                            <Tab eventKey="history" title={t('history')}>
                                                <PetHistoryComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                            </Tab>
                                        )}

                                        {hasPermission("Appointment Exam view") && (
                                            <Tab eventKey="physical-exam" title={t('Physical Exam')}>
                                                <PetPhysicalExamComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                            </Tab>
                                        )}

                                        {hasPermission("Appointment Diagnose view") && (
                                            <Tab eventKey="diagnose" title={t('Diagnose')}>
                                                <PetDiagnoseComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                            </Tab>
                                        )}
                                    </Tabs>
                                </div>
                            </Tab>
                            {hasPermission("Appointment Surgery view") && (
                                <Tab eventKey="follow-up" title={t('Follow Up')} >
                                    <PetFollowUpComponent appointmentId={appointment.id} petId={appointment.pet_id} doctorId={appointment.doctor_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}
                            {hasPermission("Appointment Surgery view") && (
                                <Tab eventKey="check-up" title={t('Check Up')} >
                                    <PetCheckUpComponent appointmentId={appointment.id} petId={appointment.pet_id} doctorId={appointment.doctor_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}
                            {hasPermission("Appointment Surgery view") && (
                                <Tab eventKey="surgery" title={t('Surgery')} >
                                    <PetSurgeryComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}

                            {hasPermission("Appointment Vaccine view") && (
                                <Tab eventKey="vaccine" title={t('vaccine')}>
                                    <PetVaccineComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}

                            {hasPermission("Appointment Deworming view") && (
                                <Tab eventKey="deworming" title={t('deworming')}>
                                    <PetDewormingComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}

                            {hasPermission("Appointment Grooming view") && (
                                <Tab eventKey="grooming" title={t('grooming')}>
                                    <PetGroomingComponent appointmentId={appointment.id} petId={appointment.pet_id} doctorId={appointment.doctor_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}

                            {hasPermission("Appointment Lab Test view") && (
                                <Tab eventKey="test" title={t('Lab Test')}>
                                    <PetLabTestComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}

                            {hasPermission("Appointment Prescription view") && (
                                <Tab eventKey="radiology" title={t('Radiology')}>
                                    <PetRadiologyComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}

                            {hasPermission("Appointment Radiology view") && (
                                <Tab eventKey="prescription" title={t('prescription')} className="mc-tabpane">
                                    <div className="mt-3">
                                        <Tabs
                                            defaultActiveKey="in_house_prescription"
                                            id="prescription-subtabs"
                                            className="mc-tabs"
                                            fill
                                            justify
                                            mountOnEnter={true}
                                            unmountOnExit={false}
                                        >
                                            <Tab eventKey="in_house_prescription" title={t('In House Prescription')}>
                                                <PetInhousePrescriptionComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                            </Tab>

                                            <Tab eventKey="home_prescription" title={t('Home Prescription')}>
                                                <PetHomePrescriptionComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                            </Tab>
                                        </Tabs>
                                    </div>
                                </Tab>
                            )}

                            {hasPermission("Appointment Radiology view") && (
                                <Tab eventKey="sale" title={t('Sale')}>
                                    <CustomerSaleComponent appointmentId={appointment.id} petId={appointment.pet_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}
                            {hasPermission("Appointment Payments view") && (
                                <Tab eventKey="invoice" title={t('Payments')} mountOnEnter={true}
                                    unmountOnExit={true}>
                                    <PetInvoiceComponent appointmentId={appointment.id} petId={appointment.pet_id} customerId={appointment.customer_id} isAppointmentOpen={isAppointmentOpen} />
                                </Tab>
                            )}
                        </Tabs>
                    </div>
                </Col>
            </Row>

            {/* EDIT PET MODAL */}
            <Modal
                show={editPetModal}
                onHide={() => setEditPetModal(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Edit Pet')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={updateForm.name}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('breed')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="breed_id"
                                        value={updateForm.breed_id}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.breed_id}
                                    >
                                        <option value="">{t('select breed')}</option>
                                        {breeds.map(parent => (
                                            <React.Fragment key={parent.id}>
                                                <option disabled style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                                                    {parent.name} {parent.type && `(${parent.type})`}
                                                </option>
                                                {parent.child_breeds?.map(child => (
                                                    <option key={child.id} value={child.id}>
                                                        {"\u00A0\u00A0\u00A0\u00A0"}{child.name} {child.type && `(${child.type})`}
                                                    </option>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.breed_id?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('customer')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="customer_id"
                                        value={updateForm.customer_id}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.customer_id}
                                    >
                                        <option value="">{t('select customer')}</option>
                                        {customers.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name} {c.type && `(${c.type})`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.customer_id?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('age')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="age"
                                        value={updateForm.age}
                                        onChange={handleUpdateChange}
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('color')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="color"
                                        value={updateForm.color}
                                        onChange={handleUpdateChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('gender')}</Form.Label>
                                    <Form.Select
                                        name="male"
                                        value={updateForm.male}
                                        onChange={handleUpdateChange}
                                    >
                                        <option value="1">{t('Male')}</option>
                                        <option value="0">{t('Female')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('weight (kg)')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="weight"
                                        value={updateForm.weight}
                                        onChange={handleUpdateChange}
                                        placeholder={t('Enter pet weight')}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('birth date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="birth_date"
                                        value={updateForm.birth_date}
                                        onChange={handleUpdateChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Check
                                        type="checkbox"
                                        label={t('is dead')}
                                        name="is_dead"
                                        checked={updateForm.is_dead}
                                        onChange={handleUpdateChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {updateForm.is_dead && (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>{t('death date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="death_date"
                                            value={updateForm.death_date}
                                            onChange={handleUpdateChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>

                    <Modal.Footer className="p-2">
                        <ButtonComponent
                            className="btn btn-secondary"
                            onClick={() => setEditPetModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            className="btn btn-primary"
                            type="submit"
                            disabled={updateLoading}
                        >
                            {updateLoading && (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            )}
                            {t('update')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}